import { Outlet, Link, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";

import appCss from "../styles.css?url";
import interWoff2 from "@fontsource-variable/inter/files/inter-latin-wght-normal.woff2?url";
import logoUrl from "@/assets/kk-logo.webp";

const BUSINESS_JSONLD = {
  "@context": "https://schema.org",
  "@type": "MedicalBusiness",
  name: "Kawamura Cirurgia Plastica LTDA",
  alternateName: "Kawamura Cirurgia Plastica",
  legalName: "Kawamura Cirurgia Plastica LTDA",
  taxID: "37.941.107/0001-44",
  url: "https://cirurgiaplasticafoz.com.br/",
  description:
    "Cirurgião plástico em Foz do Iguaçu. Abdominoplastia, lipoescultura, mamoplastia, mastopexia, rinoplastia e mommy makeover.",
  image: "https://cirurgiaplasticafoz.com.br/og-image.jpg",
  telephone: "+5545999175813",
  email: "drkelsonkawamura@gmail.com",
  address: {
    "@type": "PostalAddress",
    streetAddress: "Rua Marechal Floriano Peixoto, 960, Edifício Torre Marechal, Sala 86",
    addressLocality: "Foz do Iguaçu",
    addressRegion: "PR",
    addressCountry: "BR",
  },
  employee: {
    "@type": "Physician",
    name: "Dr. Kelson Kawamura",
    medicalSpecialty: "PlasticSurgery",
    identifier: [
      { "@type": "PropertyValue", propertyID: "CRM", value: "32174" },
      { "@type": "PropertyValue", propertyID: "RQE", value: "24787" },
    ],
    memberOf: { "@type": "Organization", name: "Sociedade Brasileira de Cirurgia Plástica (SBCP)" },
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "12:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "14:00",
      closes: "18:00",
    },
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Saturday"],
      opens: "08:00",
      closes: "12:00",
    },
  ],
  areaServed: { "@type": "City", name: "Foz do Iguaçu" },
};

function NotFoundComponent() {
  return (
    <>
      <Header />
      <main>
        <div className="kk-section">
          <div className="kk-container" style={{ textAlign: "center", maxWidth: 600, margin: "0 auto" }}>
            <span className="kk-eyebrow">Erro 404</span>
            <h1 className="kk-h1" style={{ marginTop: 12 }}>Página não encontrada</h1>
            <hr className="kk-divider" style={{ margin: "16px auto 24px" }} />
            <p className="kk-lead" style={{ margin: "0 auto 32px" }}>
              A página que você procura não existe ou foi movida. Volte para o início para continuar
              navegando.
            </p>
            <Link to="/" className="kk-button-primary">Voltar para o início</Link>
          </div>
        </div>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Cirurgião Plástico em Foz do Iguaçu | Dr. Kelson Kawamura" },
      {
        name: "description",
        content:
          "Cirurgião plástico em Foz do Iguaçu para abdominoplastia, lipoescultura, mamoplastia, mastopexia, rinoplastia e mommy makeover.",
      },
      { name: "author", content: "Dr. Kelson Kawamura" },
      { property: "og:site_name", content: "Dr. Kelson Kawamura" },
      { property: "og:title", content: "Cirurgião Plástico em Foz do Iguaçu | Dr. Kelson Kawamura" },
      {
        property: "og:description",
        content:
          "Cirurgião plástico em Foz do Iguaçu para abdominoplastia, lipoescultura, mamoplastia, mastopexia, rinoplastia e mommy makeover.",
      },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/f55f9435-9ce6-401a-a790-0e9ef3156d05" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Cirurgião Plástico em Foz do Iguaçu | Dr. Kelson Kawamura" },
      {
        name: "twitter:description",
        content:
          "Cirurgião plástico em Foz do Iguaçu para abdominoplastia, lipoescultura, mamoplastia, mastopexia, rinoplastia e mommy makeover.",
      },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/attachments/og-images/f55f9435-9ce6-401a-a790-0e9ef3156d05" },
    ],
    links: [
      { rel: "icon", type: "image/png", href: "/favicon.png" },
      { rel: "apple-touch-icon", href: "/favicon.png" },
      { rel: "preload", as: "font", type: "font/woff2", href: interWoff2, crossOrigin: "anonymous" },
      { rel: "preload", as: "image", href: logoUrl, fetchpriority: "high" },
      { rel: "stylesheet", href: appCss },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(BUSINESS_JSONLD),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="pt-BR">
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  return <Outlet />;
}
